import Swiper from 'swiper';
import { debounce } from 'lodash';
import { AccessibilityHelper } from '../GlobalAssets/js/accessibilityHelper';
import 'swiper/css';

class MultiEntryImageAndTextCarousel {
  constructor(component) {
    this.refs = {
      carousel: '.js-carousel',
      nextButton: '.js-next',
      prevButton: '.js-prev',
      slide: '.js-slide',
    };

    this.dom = {
      $component: component,
      $carousel: component.querySelector(this.refs.carousel),
      $nextButton: component.querySelector(this.refs.nextButton),
      $prevButton: component.querySelector(this.refs.prevButton),
      $slides: component.querySelectorAll(this.refs.slide),
    };

    this.state = {
      swiper: null,
      isAnimating: false,
      accessibilityHelper: new AccessibilityHelper(),
      slideChangeDebounceHandler: null,
      resizeDebounceHandler: null,
    };

    this.init();

    this.mount();
  }

  init() {
    const { dom, state } = this;

    state.slideChangeDebounceHandler = debounce((swiper) => this.onSlideChange(swiper), 500);
    state.resizeDebounceHandler = debounce(() => this.updateSlideAccessibility(), 200);

    state.swiper = new Swiper(dom.$carousel, {
      // Swiper options
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      centeredSlides: false,
      loop: true,
      watchSlidesProgress: true,
      allowTouchMove: true,
      breakpoints: {
        768: {
          allowTouchMove: false,
          watchSlidesProgress: true,
        },
        1200: {
          allowTouchMove: false,
          watchSlidesProgress: true,
        },
      },
      on: {
        init: this.animateSlide.bind(this),
        afterInit: (swiper) => {
          dom.$carousel.style.height = `${swiper.slides[0].offsetHeight}px`;
          swiper.loopFix();
          // delay 500ms to account for mobile-only transition. Not necessary 768 and above but doesn't hurt to delay either.
          setTimeout(() => this.updateSlideAccessibility(), 500);
        },
        // Need a slideChange handler for dragging on mobile
        slideChange: (swiper) => this.state.slideChangeDebounceHandler(swiper),
        slideChangeTransitionEnd: (swiper) => {
          swiper.loopFix();

          if (window.innerWidth >= 768) return;
          // since animateSlide does not get called on mobile via onNextClick and onPrevClick, need to update accessibility on slideChangeTransitionEnd
          this.updateSlideAccessibility();
        }
      },
    });
  }

  mount() {
    const { dom, state } = this;
    if (dom.$nextButton) {
      dom.$nextButton.addEventListener('click', this.onNextClick.bind(this));
    }
    if (dom.$prevButton) {
      dom.$prevButton.addEventListener('click', this.onPrevClick.bind(this));
    }

    if (dom.$slides) {
      dom.$slides.forEach($slide => {
        $slide.addEventListener('click', this.onSlideClick.bind(this));
      });
      window.addEventListener('resize', this.onWindowResize.bind(this));
    }
  }

  onSlideChange(swiper) {
    if (window.innerWidth >= 768) return;

    swiper.loopFix();
    this.updateSlideAccessibility();
  }

  onWindowResize() {
    const { dom, state } = this;
    if (!state.swiper) return;
    dom.$carousel.style.height = 'auto';

    // get max height of all slides
    let maxHeight = 0;
    state.swiper.slides.forEach(slide => {
      const slideHeight = slide.offsetHeight;
      if (slideHeight > maxHeight) {
        maxHeight = slideHeight;
      }
    });

    dom.$carousel.style.height = `${maxHeight}px`;
    state.resizeDebounceHandler();
  }

  onSlideClick(event) {
    const { dom, state, refs } = this;
    if (!state.swiper || state.isAnimating) return;

    const $clickedSlide = event.currentTarget;

    if ($clickedSlide.classList.contains('is-active')) return;

    const $activeSlide = dom.$component.querySelector(`${refs.slide}.is-active`);
    let slideIndex = parseInt($clickedSlide.dataset.swiperSlideIndex);
    const activeSlideIndex = parseInt($activeSlide.dataset.swiperSlideIndex);

    if (activeSlideIndex < slideIndex) {
      this.onNextClick();
      return;
    }

    this.onPrevClick();
  }

  onNextClick() {
    const { dom, state } = this;

    if (!state.swiper || state.isAnimating) return;

    if (window.innerWidth < 768) {
      state.swiper.slideNext(500);
      return;
    }

    state.isAnimating = true;
    dom.$nextButton.disabled = true;
    dom.$prevButton.disabled = true;
    state.swiper.slides[state.swiper.activeIndex].classList.add('is-animating-down');
    state.swiper.slides[state.swiper.activeIndex].classList.remove('is-active');
    setTimeout(() => state.swiper.slideNext(650), 550);
    setTimeout(this.animateSlide.bind(this), 1150);
  }

  onPrevClick() {
    const { dom, state } = this;
    if (!state.swiper || state.isAnimating) return;

    if (window.innerWidth < 768) {
      state.swiper.slidePrev(500);
      return;
    }

    state.isAnimating = true;
    dom.$nextButton.disabled = true;
    dom.$prevButton.disabled = true;
    state.swiper.slides[state.swiper.activeIndex].classList.add('is-animating-down');
    state.swiper.slides[state.swiper.activeIndex].classList.remove('is-active');
    setTimeout(() => state.swiper.slidePrev(650), 550);
    setTimeout(this.animateSlide.bind(this), 1150);
  }

  animateSlide () {
    const { dom, state } = this;

    if (!state.swiper) return;
    state.swiper.slides.forEach(slide => {
      slide.classList.remove('is-active');
      slide.classList.remove('is-animating-up');
      slide.classList.remove('is-animating-down');
    });
    state.swiper.slides[state.swiper.activeIndex].classList.add('is-animating-up');
    state.swiper.slides[state.swiper.activeIndex].classList.add('is-active');

    setTimeout(() => {
      state.swiper.slides[state.swiper.activeIndex].classList.remove('is-animating-up');
      dom.$prevButton.disabled = false;
      dom.$nextButton.disabled = false;
      state.isAnimating = false;
      console.log('animation complete');
      this.updateSlideAccessibility();
    }, 300);

  }

  updateSlideAccessibility() {
    const { dom, state } = this;
    const containerRect = dom.$carousel.getBoundingClientRect();
    const visibleLeft = Math.max(containerRect.left, 0);
    const visibleRight = Math.min(containerRect.right, window.innerWidth);

    dom.$slides.forEach((slide) => {
      const slideRect = slide.getBoundingClientRect();
      const visibleWidth = Math.min(slideRect.right, visibleRight) - Math.max(slideRect.left, visibleLeft);
      const isVisible = visibleWidth >= slideRect.width * 0.5;
      slide.setAttribute('aria-hidden', !isVisible);
      state.accessibilityHelper.updateElementAccessibility(slide, isVisible);
    });
  }
}
document.querySelectorAll('.js-multi-entry-image-and-text-carousel')
  .forEach(el => new MultiEntryImageAndTextCarousel(el));

